/**
 * Recently viewed products strip for the mobile menu drawer.
 * Cards match the Shop mega-menu featured product layout.
 */

import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

class MenuDrawerRecentlyViewed extends HTMLElement {
  /** @type {boolean} */
  #loading = false;

  /** @type {HTMLDetailsElement | null} */
  #drawerDetails = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#drawerDetails = this.closest('details.menu-drawer-container');
    this.#drawerDetails?.addEventListener('toggle', this.#onDrawerToggle);
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#drawerDetails?.removeEventListener('toggle', this.#onDrawerToggle);
  }

  /**
   * @param {Event} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('[data-recently-viewed-clear]')) return;

    event.preventDefault();
    RecentlyViewed.clearProducts();
    this.dataset.loaded = 'false';
    this.dataset.productIds = '';
    this.#hide();
    const list = this.querySelector('[data-recently-viewed-list]');
    if (list) list.replaceChildren();
  };

  #onDrawerToggle = () => {
    if (!this.#drawerDetails?.open) return;

    const productIds = RecentlyViewed.getProducts().filter(Boolean);
    const signature = productIds.join(',');

    if (productIds.length === 0) {
      if (this.dataset.designMode === 'true') {
        const list = this.querySelector('[data-recently-viewed-list]');
        if (list?.querySelector('.menu-drawer__recently-viewed-item')) {
          this.#show();
          return;
        }
      }
      this.#hide();
      return;
    }

    if (this.dataset.loaded === 'true' && this.dataset.productIds === signature) {
      this.#show();
      return;
    }

    this.dataset.loaded = 'false';
    this.#load();
  };

  #hide() {
    this.hidden = true;
    this.setAttribute('hidden', '');
  }

  #show() {
    this.hidden = false;
    this.removeAttribute('hidden');
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #upgradeImageUrl(url) {
    if (!url) return url;
    return url
      .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|\d+x\d*|\d+x)/g, '_320x')
      .replace(/([?&])width=\d+/g, '$1width=320')
      .replace(/([?&])height=\d+/g, '$1');
  }

  /**
   * @param {Element} item
   * @returns {HTMLLIElement | null}
   */
  #buildCard(item) {
    const productId = item.getAttribute('data-product-id');
    if (!productId) return null;

    const link = item.querySelector('.predictive-search-results__product-row-link');
    const img = item.querySelector('img');
    const title = item.querySelector('.predictive-search-results__product-row-title');
    const price = item.querySelector('.predictive-search-results__product-row-price');
    const href = link?.getAttribute('href')?.trim();
    if (!href || href === '#') return null;

    const shopNowLabel = this.dataset.shopNowLabel?.trim() || 'Shop now';
    const titleText = title?.textContent?.trim() || '';
    const priceText = price?.textContent?.trim() || '';

    const li = document.createElement('li');
    li.className = 'menu-drawer__recently-viewed-item pj-mega-menu-drawer__featured-item';

    const a = document.createElement('a');
    a.className = 'pj-mega-menu-featured pj-mega-menu-featured--drawer';
    a.href = href;

    const media = document.createElement('span');
    media.className = 'pj-mega-menu-featured__media';
    media.setAttribute('aria-hidden', 'true');

    if (img instanceof HTMLImageElement) {
      const clone = /** @type {HTMLImageElement} */ (img.cloneNode(true));
      clone.className = 'pj-mega-menu-featured__image';
      clone.loading = 'lazy';
      clone.decoding = 'async';
      clone.removeAttribute('width');
      clone.removeAttribute('height');
      clone.removeAttribute('sizes');
      clone.removeAttribute('srcset');
      clone.src = this.#upgradeImageUrl(clone.currentSrc || clone.src);
      media.appendChild(clone);
    }

    const body = document.createElement('span');
    body.className = 'pj-mega-menu-featured__body';

    const titleEl = document.createElement('span');
    titleEl.className = 'pj-mega-menu-featured__title';
    titleEl.textContent = titleText;
    body.appendChild(titleEl);

    if (priceText) {
      const priceEl = document.createElement('span');
      priceEl.className = 'pj-mega-menu-featured__price';
      priceEl.textContent = priceText.startsWith('AED') ? priceText : `AED ${priceText}`;
      body.appendChild(priceEl);
    }

    const cta = document.createElement('span');
    cta.className = 'pj-mega-menu-featured__cta';
    cta.append(document.createTextNode(shopNowLabel), document.createTextNode(' '));

    const arrow = document.createElement('span');
    arrow.className = 'pj-mega-menu__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    cta.appendChild(arrow);
    body.appendChild(cta);

    a.append(media, body);
    li.appendChild(a);
    return li;
  }

  async #load() {
    if (this.#loading) return;

    const list = this.querySelector('[data-recently-viewed-list]');
    if (!list) return;

    const productIds = RecentlyViewed.getProducts().filter(Boolean);
    const signature = productIds.join(',');

    if (productIds.length === 0) {
      if (this.dataset.designMode === 'true' && list.querySelector('.menu-drawer__recently-viewed-item')) {
        this.#show();
        return;
      }
      this.#hide();
      return;
    }

    if (this.dataset.loaded === 'true' && this.dataset.productIds === signature) {
      this.#show();
      return;
    }

    this.#loading = true;

    try {
      const searchPath = window.Theme?.routes?.search_url || '/search';
      const url = new URL(searchPath, window.location.origin);
      url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('type', 'product');

      const html = await sectionRenderer.getSectionHTML('predictive-search', false, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = [
        ...doc.querySelectorAll('.predictive-search-results__card--product[data-product-id]'),
      ].filter((item) => Boolean(item.getAttribute('data-product-id')));

      if (items.length === 0) {
        this.#hide();
        return;
      }

      /** @type {Map<string, Element>} */
      const byId = new Map();
      items.forEach((item) => {
        byId.set(String(item.getAttribute('data-product-id')), item);
      });

      const fragment = document.createDocumentFragment();
      for (const id of productIds) {
        const item = byId.get(String(id));
        if (!item) continue;
        const card = this.#buildCard(item);
        if (card) fragment.appendChild(card);
      }

      if (!fragment.childNodes.length) {
        items.forEach((item) => {
          const card = this.#buildCard(item);
          if (card) fragment.appendChild(card);
        });
      }

      if (!fragment.childNodes.length) {
        this.#hide();
        return;
      }

      list.replaceChildren(fragment);
      this.dataset.loaded = 'true';
      this.dataset.productIds = signature;
      this.#show();
    } catch (error) {
      console.warn('[menu-drawer-recently-viewed] Failed to load products', error);
      this.#hide();
    } finally {
      this.#loading = false;
    }
  }
}

if (!customElements.get('menu-drawer-recently-viewed')) {
  customElements.define('menu-drawer-recently-viewed', MenuDrawerRecentlyViewed);
}
